import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getTaskStatuses,
  createTaskStatus,
  updateTaskStatus,
  deleteTaskStatus,
} from "../services/taskStatusService";
import { useAppContext } from "../context/AppContext";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { resolveRoleName } from "../utils/auth";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const TaskStatusManagementPage = () => {
  const { setActiveSection } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ description: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTaskStatuses();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load task statuses:", err);
      setError(err.message || "Failed to load Task Statuses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const desc = (item.description || "").toLowerCase();
      return desc.includes(searchTerm.toLowerCase());
    });
  }, [items, searchTerm]);

  const openCreateModal = () => {
    setActiveDropdown(null);
    setFormData({ description: "" });
    setFormError(null);
    setModalMode("create");
    setSelected(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setActiveDropdown(null);
    setFormData({ description: item.description || "" });
    setFormError(null);
    setModalMode("edit");
    setSelected(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setFormError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const statusLabel = String(formData.description || "").trim();
    if (!statusLabel) {
      setFormError("Status is required");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = { description: statusLabel };
      if (modalMode === "create") {
        await createTaskStatus(payload);
      } else if (modalMode === "edit" && selected) {
        await updateTaskStatus(selected.id, payload);
      }
      await loadData();
      closeModal();
    } catch (err) {
      console.error("Failed to save task status:", err);
      setFormError(err.message || "Failed to save Task Status");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!item) return;
    const label = item.description || `ID ${item.id}`;
    const shouldDelete = window.confirm(
      `Are you sure you want to delete "${label}"? This action cannot be undone.`,
    );
    if (!shouldDelete) return;

    setDeletingId(item.id);
    try {
      await deleteTaskStatus(item.id);
      toast.success(`Task status "${label}" deleted successfully.`);
      await loadData();
    } catch (err) {
      console.error("Failed to delete task status:", err);
      toast.error(err.message || "Failed to delete Task Status");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading Task Statuses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <p>Error: {error}</p>
          <button className="retry-btn" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Task Status</h2>
            <p className="page-subtitle">Manage statuses for tasks</p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search statuses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="secondary-btn sm" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            </div>
            <button className="primary-btn" onClick={openCreateModal}>
              + Create Status
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="kpi-table-container">
          {filtered.length === 0 ? (
            <div className="no-results">
              <p>No task statuses found.</p>
              {items.length === 0 && (
                <button className="create-btn" onClick={openCreateModal}>
                  Create your first status
                </button>
              )}
            </div>
          ) : (
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>
                      {item.description || <span className="empty-text">-</span>}
                    </td>
                    <td className="actions-cell">
                      <div
                        className="actions-menu-wrapper"
                        style={{ position: "relative", display: "inline-block" }}
                      >
                        <button
                          className="action-menu-btn"
                          aria-haspopup="true"
                          aria-expanded={activeDropdown === item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeDropdown === item.id) {
                              setActiveDropdown(null);
                            } else {
                              setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                              setActiveDropdown(item.id);
                            }
                          }}
                        >
                          Actions ▼
                        </button>
                        {activeDropdown === item.id && (
                          <div
                            className={`action-dropdown-menu${
                              dropdownDirection === "up" ? " dropdown-up" : ""
                            }`}
                            role="menu"
                          >
                            <button
                              className="action-menu-item"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(item);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="action-menu-item danger"
                              role="menuitem"
                              disabled={deletingId === item.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(null);
                                handleDelete(item);
                              }}
                            >
                              {deletingId === item.id ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === "create" ? "Create Task Status" : "Edit Task Status"}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Status *</label>
                <input
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="e.g. In Progress"
                  autoFocus
                />
              </div>
              {formError && <div className="error-text">{formError}</div>}
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default TaskStatusManagementPage;

